import clsx from 'clsx';
import { MouseEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AssignmentFeedback } from 'src/components/assignments/assignment-feedback';
import { AssignmentStatusBadge } from 'src/components/assignments/assignment-status';
import { AmplifyButton } from 'src/components/common/amplify-button';
import { EmptySlotDashIcon } from 'src/components/icons';
import { Tooltip } from 'src/components/tooltip';
import { msAssignmentStatuses } from 'src/constants/xApi.constants';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useFeedback } from 'src/hooks/use-feedback.hook';
import { useFormatAssignmentDueDate } from 'src/hooks/use-format-assignment-due-date';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import { useScore } from 'src/hooks/use-score.hook';
import { useTrimmedElementCheck } from 'src/hooks/use-trimmed-element-check';
import {
  AssignmentStatus,
  CardstackAssignment,
  CurriculumAssignmentContent,
} from 'src/interfaces/assignment.types';
import { XApiEventKeys } from 'src/interfaces/event.types';
import { getAmpConfigFlags } from 'src/services/amp-config.service';
import eventService from 'src/services/event.service';
import { useCurrentGradeSelector } from 'src/store/features';
import { getMSAssignmentStatusOption } from 'src/utils/assignments.utils';
import { handleSpaceClick } from 'src/utils/keyboard.utils';
import { getTheme } from 'src/utils/theme.utils';
import { curriculumURLs } from 'src/utils/url.utils';
import { generateAssignmentDataForEventPayload } from 'src/utils/xapi.utils';

import './activity.scss';

export type ActivityProps = {
  content: CurriculumAssignmentContent;
  dueDate: Date | null;
  activityStatus: AssignmentStatus;
  assignment: CardstackAssignment;
};

export const Activity = ({
  content,
  dueDate,
  activityStatus,
  assignment,
}: ActivityProps) => {
  const { t } = useTranslation();

  const formatAssignmentDueDate = useFormatAssignmentDueDate();
  const userCurrentGrade = useCurrentGradeSelector();
  const isMiddleSchoolView = useIsMiddleSchoolView();
  const commonXapiContextPayload = useCommonXapiContextPayloadHook();

  const theme = getTheme(userCurrentGrade, isMiddleSchoolView);

  const {
    cardstackTitle,
    cardstackOrdinalNumber,
    cardstackStatus,
    unitKey,
    lessonKey,
    chapterKey,
    cardstackKey,
  } = content;

  const { feedback } = useFeedback(cardstackKey);
  const { score } = useScore(cardstackKey);

  const activityNameRef = useRef<HTMLSpanElement>(null);

  const isActivityNameTrimmed = useTrimmedElementCheck(activityNameRef);

  const cardstackURL = curriculumURLs.getCardstackURL(
    unitKey,
    chapterKey,
    lessonKey,
    cardstackKey,
  );

  const { isHandedInmCLASSAssessment } = assignment;

  const isDone = cardstackStatus === AssignmentStatus.READ;

  const cardstackNumber = cardstackOrdinalNumber
    ? `${cardstackOrdinalNumber}. `
    : '';

  const preparedCardstackTitle = `${cardstackNumber}${cardstackTitle}`;

  const assignmentStatus = getMSAssignmentStatusOption(activityStatus);

  // TODO: Remove the feature flag after RFS.
  const isUpdatedMSViewEnabled = getAmpConfigFlags().isUpdatedMSViewEnabled;

  const handleActivityBtnClick = async (e: MouseEvent) => {
    e.preventDefault();

    try {
      await eventService.send(
        XApiEventKeys.openedAssignment,
        {
          result: {
            destinationUrl: cardstackURL,
          },
        },
        {
          result: {
            isRightClick: e.type === 'contextmenu',
            ...commonXapiContextPayload,
            ...generateAssignmentDataForEventPayload({
              assignmentBusinessKey: assignment.assignmentBk,
              assignmentClassBk: assignment.assignmentClassBks,
              classGrade: userCurrentGrade,
              content: content,
              assignmentStatus: msAssignmentStatuses[assignmentStatus],
              cardstackBusinessKey: cardstackKey,
            }),
          },
        },
      );
    } catch (error) {
      console.error('Error sending opened assignment event', error);
    } finally {
      if (e.type !== 'contextmenu') {
        window.location.assign(cardstackURL);
      }
    }
  };

  if (isMiddleSchoolView) {
    return (
      <div
        className="activity activity--middle-school"
        data-testid="middle-school-cardstack-activity"
      >
        <div className="activity__content activity__content--middle-school">
          <div className="activity__name-wrapper">
            <Tooltip
              title={cardstackTitle ?? ''}
              classNames={{ tooltip: 'sh-mobile-tooltip-bottom-left' }}
              suppress={!isActivityNameTrimmed}
            >
              <span
                ref={activityNameRef}
                className="activity__name activity__name--middle-school"
                tabIndex={isActivityNameTrimmed ? 0 : -1}
              >
                {cardstackTitle}
              </span>
            </Tooltip>
          </div>
          <AssignmentStatusBadge assignmentStatus={activityStatus} />
          <AssignmentFeedback
            feedback={feedback}
            score={score}
            assignment={assignment}
            commonXapiContextPayload={commonXapiContextPayload}
            assignmentStatus={assignmentStatus}
          />
          <EmptySlotDashIcon />
          <span className="activity__button">
            {activityStatus === AssignmentStatus.READ ||
            activityStatus === AssignmentStatus.HANDED_IN ? (
              <AmplifyButton
                onClick={handleActivityBtnClick}
                onContextMenu={handleActivityBtnClick}
                href={cardstackURL}
                ariaLabel="Open activity"
                variant="secondary"
                disabled={isHandedInmCLASSAssessment}
              >
                {t('Open')}
              </AmplifyButton>
            ) : (
              <AmplifyButton
                onClick={handleActivityBtnClick}
                onContextMenu={handleActivityBtnClick}
                href={cardstackURL}
                ariaLabel="Open activity"
                disabled={isHandedInmCLASSAssessment}
                variant={isUpdatedMSViewEnabled ? 'blue' : 'primary'}
              >
                {t('Start')}
              </AmplifyButton>
            )}
          </span>
        </div>
      </div>
    );
  }

  return (
    <a
      className={clsx('activity', {
        'activity--done': isDone,
      })}
      onKeyDown={handleSpaceClick}
      href={cardstackURL}
      data-testid="cardstack-activity"
    >
      <span className="activity__content">
        <Tooltip
          title={preparedCardstackTitle}
          classNames={{ tooltip: 'sh-mobile-tooltip-top-center' }}
          suppress={!isActivityNameTrimmed}
        >
          <span
            ref={activityNameRef}
            className="activity__name"
            tabIndex={isActivityNameTrimmed ? 0 : -1}
          >
            {preparedCardstackTitle}
          </span>
        </Tooltip>
        <time className="activity__date">
          {dueDate && formatAssignmentDueDate(dueDate)}
        </time>
        <span
          className={clsx('activity__feedback', `activity__feedback--${theme}`)}
        >
          <AssignmentFeedback
            feedback={feedback}
            score={score}
            assignment={assignment}
            commonXapiContextPayload={commonXapiContextPayload}
            assignmentStatus={assignmentStatus}
          />
        </span>
      </span>
    </a>
  );
};
